import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Select,
  FormControl,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useForm, Controller } from "react-hook-form";
import DatePickerInput from "components/CustomDateInput/CustomDateInput";
import SearchTable2 from "components/Tables/SearchTable2";
import { columnsDataOnlinePaymentTransactionReport } from "variables/columnsData";
import { useOnlinePaymentTransactionReport } from "hooks/useOnlinePaymentTransactionReport";
import { useBookingActions } from "hooks/useBookingActions";
import { bookingStatus, paymentGateways, paymentStatuses } from 'variables/general';
import { onlinePaymentTransactionAtom } from "state/online-payment-transaction";
import { totalRow } from "state/roles-permissions";

import {
  listShowAtom,
  venueListAtom,
  zoneListAtom,
  showTimeListAtom,
} from "../../state/show";
import dayjs from "dayjs";
import { RxReset } from "react-icons/rx";
import { RiFileExcel2Fill } from "react-icons/ri";
import { BsSearch } from "react-icons/bs";

function DataTableOnlinePaymentTransactionReport() {
    const { getTransactionReport, exportTransactionReport } = useOnlinePaymentTransactionReport();
    const [isExportExcel, setIsExportExcel] = useState(false);
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const listShow = useRecoilValue(listShowAtom);
    const venueList = useRecoilValue(venueListAtom);
    const listZone = useRecoilValue(zoneListAtom);
    const showTimeList = useRecoilValue(showTimeListAtom);
    const bookingActions = useBookingActions();
    const [isFetching, setIsFetching] = useState(false);
    const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
    const transactionData = useRecoilValue(onlinePaymentTransactionAtom);
    const totalRows = useRecoilValue(totalRow);
    const [hasSearched, setHasSearched] = useState(false);
    const [isFilterDirty, setIsFilterDirty] = useState(false);
    const setTransactionData = useSetRecoilState(onlinePaymentTransactionAtom);
    const setTotalRows = useSetRecoilState(totalRow);

    const {
        register: registerSearch,
        handleSubmit: handleSubmitSearch,
        control: controlSearch,
        watch: watchSearch,
        reset: resetSearch,
    } = useForm({
        defaultValues: {
            from_showdate: new Date(),
            to_showdate: new Date(),
        },
    });

    const allSearchValues = watchSearch();

    useEffect(() => {
        if (hasSearched) {
            setIsFilterDirty(true);
        }
    }, [JSON.stringify(allSearchValues)]);

    const onSubmitSearch = async (formValues) => {
        // Validate: at least one date range must be selected
        const hasShowDate = formValues.from_showdate || formValues.to_showdate;
        const hasTransactionDate = formValues.transaction_date_from || formValues.transaction_date_to;
        if (!hasShowDate && !hasTransactionDate) {
            toast({
                description: 'Please select Date range (Show Date/Transaction Date) before searching.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: 'top-right',
            });
            if (isExportExcel) setIsExportExcel(false);
            return;
        }
        // If export requested, build params and call export API
        if (isExportExcel) {
            setIsExportExcel(false);
            const params = {
                show_id: formValues.showSearch || undefined,
                venue_id: formValues.venueSearch || undefined,
                zone_id: formValues.zoneSearch || undefined,
                show_time: formValues.showTimeSearch || undefined,
                transaction_date_from: formValues.transaction_date_from
                    ? new Date(new Date(formValues.transaction_date_from).setHours(0, 0, 0, 0)).toISOString()
                    : undefined,
                transaction_date_to: formValues.transaction_date_to
                    ? new Date(new Date(formValues.transaction_date_to).setHours(23, 59, 59, 999)).toISOString()
                    : undefined,
                show_date_from: formValues.from_showdate
                    ? dayjs(formValues.from_showdate).format('YYYY-MM-DD')
                    : undefined,
                show_date_to: formValues.to_showdate
                    ? dayjs(formValues.to_showdate).format('YYYY-MM-DD')
                    : undefined,
                booking_status: formValues.bookingStatusSearch || undefined,
                status: formValues.paymentStatusSearch || undefined,
                payment_gateway: formValues.paymentGatewaySearch || undefined,
            };
            try {
                setIsFetching(true);
                const res = await exportTransactionReport(params);
                const contentDisposition = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'];
                let filename = `transaction_report_${dayjs().format('YYYYMMDD')}`;
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename\*=[^']*''([^;\n\r]+)/i) || contentDisposition.match(/filename="?([^";]+)"?/i);
                    if (match && match[1]) filename = decodeURIComponent(match[1]);
                }
                // Ensure filename has an extension (xlsx fallback)
                if (!/\.[a-zA-Z0-9]+$/.test(filename)) {
                    filename = `${filename}.xlsx`;
                }
                // If axios returned a Blob, use it directly; otherwise create one
                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: (res.data && res.data.type) || 'application/octet-stream' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename || 'transaction_report');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (err) {
                console.error('Export failed', err);
            } finally {
                setIsFetching(false);
            }
            setRefreshPagingDefault((v) => !v);
                return;
        }

        let pageSize = 10;
        try {
            const s = localStorage && localStorage.getItem('currentPageSizeStore');
            if (s) pageSize = parseInt(s, 10) || 10;
        } catch (e) {
        }
        setRefreshPagingDefault((v) => !v);
        setIsFilterDirty(false);
        recallApi({ pageIndex: 0, pageSize }, true);
    };

    const recallApi = async (fetchDataOptions, isFromSearch) => {
        setIsFetching(true);
        const pageIndex = fetchDataOptions?.pageIndex ?? 0;
        const pageSize = fetchDataOptions?.pageSize ?? 10;
        const params = {
            show_id: watchSearch("showSearch") || undefined,
            venue_id: watchSearch("venueSearch") || undefined,
            zone_id: watchSearch("zoneSearch") || undefined,
            show_time: watchSearch("showTimeSearch") || undefined,
            transaction_date_from: watchSearch("transaction_date_from")
                ? new Date(new Date(watchSearch("transaction_date_from")).setHours(0, 0, 0, 0)).toISOString()
                : undefined,
            transaction_date_to: watchSearch("transaction_date_to")
                ? new Date(new Date(watchSearch("transaction_date_to")).setHours(23, 59, 59, 999)).toISOString()
                : undefined,
            show_date_from: watchSearch("from_showdate")
                ? dayjs(watchSearch("from_showdate")).format("YYYY-MM-DD")
                : undefined,
            show_date_to: watchSearch("to_showdate")
                ? dayjs(watchSearch("to_showdate")).format("YYYY-MM-DD")
                : undefined,
            booking_status: watchSearch("bookingStatusSearch") || undefined,
            status: watchSearch("paymentStatusSearch") || undefined,
            payment_gateway: watchSearch("paymentGatewaySearch") || undefined,
            // include sorting from table header (asc/desc and field)
            sort_type: fetchDataOptions?.sort_type || "",
            sort_field: fetchDataOptions?.sort_field || "",
            offset: pageIndex * pageSize,
            limit: pageSize,
        };
        if (hasSearched || isFromSearch) {
            await getTransactionReport(params, pageIndex, pageSize);
        }
        setIsFetching(false);
        if (isFromSearch) {
            setHasSearched(true);
        }
    };

    useEffect(() => {
        bookingActions.getListShow({ status: 1 }, 0, 100);
        bookingActions.getListVenue({ status: 1 }, 0, 100);
        bookingActions.getListZones({ status: 1 }, 0, 100);
        bookingActions.getListShowTime({ status: 1 }, 0, 100);
        setTransactionData([]);
        setTotalRows((prev) => ({ ...prev, onlinePaymentTransaction: 0 }));
    }, []);

    return (
        <Card px="0px">
        <CardHeader px="22px" mb="12px">
            <Flex direction="column" display="block">
            <Text
                float="left"
                color={textColor}
                fontSize="lg"
                fontWeight="bold"
                mb="6px"
            >
                Online Payment Transaction Report
            </Text>
            </Flex>
        </CardHeader>
        <CardBody>
            <Card>
            <form
                id="transaction-search-form"
                onSubmit={handleSubmitSearch(onSubmitSearch)}
            >
                <Flex
                direction="row"
                alignItems="center"
                mb={3}
                gap={4}
                flexWrap={"wrap"}
                >
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Show Date From
                    </FormLabel>
                    <Controller
                    name="from_showdate"
                    control={controlSearch}
                    render={({ field }) => (
                        <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch("from_showdate")}
                        selectsStart
                        endDate={watchSearch("to_showdate")}
                        minDate={new Date("01/01/2020")}
                        showTimeSelect={false}
                        dateFormat="dd/MM/yyyy"
                        />
                    )}
                    />
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Show Date To
                    </FormLabel>
                    <Controller
                    name="to_showdate"
                    control={controlSearch}
                    render={({ field }) => (
                        <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch("to_showdate")}
                        selectsEnd
                        startDate={watchSearch("from_showdate")}
                        minDate={watchSearch("from_showdate")}
                        showTimeSelect={false}
                        dateFormat="dd/MM/yyyy"
                        />
                    )}
                    />
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Transaction Date From
                    </FormLabel>
                    <Controller
                    name="transaction_date_from"
                    control={controlSearch}
                    render={({ field }) => (
                        <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch("transaction_date_from")}
                        selectsStart
                        endDate={watchSearch("transaction_date_to")}
                        minDate={new Date("01/01/2020")}
                        showTimeSelect={false}
                        dateFormat="dd/MM/yyyy"
                        />
                    )}
                    />
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Transaction Date To
                    </FormLabel>
                    <Controller
                    name="transaction_date_to"
                    control={controlSearch}
                    render={({ field }) => (
                        <DatePickerInput
                        onChange={(value) => field.onChange(value)}
                        selected={watchSearch("transaction_date_to")}
                        selectsEnd
                        startDate={watchSearch("transaction_date_from")}
                        minDate={watchSearch("transaction_date_from")}
                        showTimeSelect={false}
                        dateFormat="dd/MM/yyyy"
                        />
                    )}
                    />
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Show
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="showSearch"
                    {...registerSearch("showSearch")}
                    >
                    <option value="">All</option>
                    {listShow.map((item) => (
                        <option key={item.id} value={item.id}>
                        {item.name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Venue
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="venueSearch"
                    {...registerSearch("venueSearch")}
                    >
                    <option value="">All</option>
                    {venueList.map((item) => (
                        <option key={item.id} value={item.id}>
                        {item.name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Zone
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="zoneSearch"
                    {...registerSearch("zoneSearch")}
                    >
                    <option value="">All</option>
                    {listZone.map((item) => (
                        <option key={item.id} value={item.id}>
                        {item.name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Show Time
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="showTimeSearch"
                    {...registerSearch("showTimeSearch")}
                    >
                    <option value="">All</option>
                    {showTimeList.map((item) => (
                        <option key={item.name} value={item.name}>
                        {item.name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Booking Status
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="bookingStatusSearch"
                    {...registerSearch("bookingStatusSearch")}
                    >
                    {bookingStatus.map((item, index) => (
                        <option key={index} value={item.Id}>
                            {item.Name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Payment Gateway
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="paymentGatewaySearch"
                    {...registerSearch("paymentGatewaySearch")}
                    >
                    <option value="">All</option>
                    {paymentGateways.map((item) => (
                        <option key={item.Id} value={item.Id}>
                        {item.Name}
                        </option>
                    ))}
                    </Select>
                </Box>
                <Box direction="row" alignItems="center" display="block">
                    <FormLabel fontWeight="semibold" fontSize="xs" ms="4px">
                    Payment Status
                    </FormLabel>
                    <Select
                    ms="4px"
                    type="text"
                    size="sm"
                    name="paymentStatusSearch"
                    {...registerSearch("paymentStatusSearch")}
                    >
                    <option value="">All</option>
                    {paymentStatuses.map((item) => (
                        <option key={item.Id} value={item.Id}>
                        {item.Name}
                        </option>
                    ))}
                    </Select>
                </Box>
                </Flex>
                <Flex justify="center" align="center" gap="8px" mt={3}>
                    <Button
                        leftIcon={<RxReset />}
                        variant="primary"
                        w="88px"
                        h="33px"
                        onClick={() => {
                            resetSearch({
                                from_showdate: new Date(),
                                to_showdate: new Date(),
                            });
                            setDateValidationError('');
                            setHasSearched(false);
                            setIsFilterDirty(false);
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        leftIcon={<RiFileExcel2Fill />}
                        variant="primary"
                        w="100px"
                        h="33px"
                        isDisabled={!hasSearched || isFilterDirty || !transactionData || transactionData.length === 0}
                        onClick={() => setIsExportExcel(true)}
                        type="submit"
                    >
                        Export Excel
                    </Button>
                    <Button
                        leftIcon={<BsSearch />}
                        variant="primary"
                        w="88px"
                        h="33px"
                        type="submit"
                    >
                        Search
                    </Button>
                </Flex>
            </form>
            </Card>
            <SearchTable2
            tableData={transactionData}
            columnsData={columnsDataOnlinePaymentTransactionReport}
            totalRow={totalRows.onlinePaymentTransaction || 0}
            loadData={(fetchDataOptions) => recallApi(fetchDataOptions, false)}
            refreshPagingDefault={refreshPagingDefault}
            isFetching={isFetching}
            />
        </CardBody>
        </Card>
    );
}

export default DataTableOnlinePaymentTransactionReport;